import { useState } from 'react';
import { ChefHat } from 'lucide-react';
import { imageSrcSet, imageUrl, type ImageRef } from '@dishly/shared';
import { cx } from '../lib/cx';

const PLACEHOLDER_HUES = [12, 28, 40, 145, 200, 330];

function hueFor(text: string): number {
  let hash = 0;
  for (const char of text) hash = (hash * 31 + char.charCodeAt(0)) | 0;
  return PLACEHOLDER_HUES[Math.abs(hash) % PLACEHOLDER_HUES.length]!;
}

export function ImagePlaceholder({ name, className }: { name: string; className?: string }) {
  const hue = hueFor(name);
  return (
    <div
      aria-hidden
      className={cx('flex items-center justify-center', className)}
      style={{ background: `linear-gradient(135deg, hsl(${hue} 70% 82%), hsl(${hue + 20} 60% 65%))` }}
    >
      <ChefHat className="size-1/4 max-h-16 max-w-16 text-white/80" strokeWidth={1.5} />
    </div>
  );
}

interface RecipeImageProps {
  image: ImageRef | null;
  alt: string;
  /** The `sizes` attribute: how wide the image is displayed. */
  sizes: string;
  className?: string;
  eager?: boolean;
}

/** Responsive recipe photo with a colour placeholder; falls back gracefully offline. */
export function RecipeImage({ image, alt, sizes, className, eager }: RecipeImageProps) {
  const [failed, setFailed] = useState(false);
  if (!image || failed) return <ImagePlaceholder name={alt} className={className} />;
  return (
    <img
      src={imageUrl(image.id, 800)}
      srcSet={imageSrcSet(image.id)}
      sizes={sizes}
      width={image.w}
      height={image.h}
      alt={alt}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      onError={() => setFailed(true)}
      className={cx('object-cover', className)}
      style={{ backgroundColor: image.color ?? undefined }}
    />
  );
}
